import { useEffect, useRef, useState } from 'react';
import { FiAlertCircle, FiMessageCircle } from 'react-icons/fi';
import { useChat } from '../../contexts/ChatContext';
import { useMemberAuth } from '../../contexts/MemberAuthContext';
import { useToast } from '../common/Toast';
import { chatService } from '../../services/chatApi';
import ReactionBar from './ReactionBar';
import AttachmentList from './AttachmentList';
import ReplyQuote from './ReplyQuote';
import MessageActions from './MessageActions';
import Avatar from './Avatar';
import { useLongPress } from '../../hooks/useLongPress';
import { formatTime, dayLabel } from '../../utils/chatTime';
import { cn } from '../../utils/helpers';

/*
 * The conversation reads as bubbles on paper: the member's own on the right in
 * the palette's pale blue, everyone else's on the left in white on a warm
 * off-white sheet, separated by sand hairlines rather than shadow.
 */
const SURFACE = 'bg-neutral-50';

/** Bubbles are square-ish and bordered rather than tailed and shadowed. */
const BUBBLE_BASE = 'rounded-[10px] px-3.5 py-2';

/** Typing dots — one animation, staggered per dot. */
const TYPING_DOTS = [0, 150, 300];

export default function MessageList() {
  const {
    messages, loading, typingMembers, timezone,
    openThread, openThreadById, togglePin, toggleReaction,
  } = useChat();

  // Which message is showing its action bar via long-press. Hover is handled in
  // CSS; this only tracks the touch case, so a tap elsewhere can dismiss it.
  const [touchedId, setTouchedId] = useState(null);

  /*
   * One hook for the whole list, not one per row — a hook cannot be called
   * inside a map, and a fresh timer ref per render would be lost between
   * pointerdown and the timer firing.
   *
   * The row records which message the press started on; the callback reads it
   * when the timer completes.
   */
  const pressedId = useRef(null);
  const longPress = useLongPress(() => setTouchedId(pressedId.current));
  const { member } = useMemberAuth();
  const toast = useToast();
  const bottomRef = useRef(null);
  const containerRef = useRef(null);
  const wasAtBottom = useRef(true);

  // Only autoscroll when the member was already at the bottom. Yanking someone
  // back down while they are reading scrollback is the classic chat annoyance.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    if (wasAtBottom.current) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [messages, typingMembers]);

  const handleScroll = () => {
    const container = containerRef.current;
    if (!container) return;
    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    wasAtBottom.current = distanceFromBottom < 80;
  };

  /**
   * Flag a message for a moderator.
   *
   * The reason is optional to the API, but a report with no context is hard to
   * act on, so the prompt asks for one. Cancel returns null and files nothing;
   * an empty string is a deliberate "no reason given". Reporting the same
   * message twice updates the existing report rather than filing a second one,
   * so there is no need to guard against a double tap here.
   */
  const reportMessage = async (message) => {
    const who = message.sender?.displayName || 'this member';
    const reason = window.prompt(
      `Report this message from ${who} to a moderator?\n\nWhat is wrong with it? (optional)`
    );
    if (reason === null) return;

    try {
      await chatService.reportMessage(message.id, reason);
      toast.success('Reported. A moderator will review it.');
    } catch (err) {
      toast.error(err.message);
    }
  };

  if (loading && messages.length === 0) {
    return (
      <div className={cn(SURFACE, 'flex-1 flex items-center justify-center text-sm text-primary/40')}>
        Loading messages…
      </div>
    );
  }

  let lastDay = null;

  return (
    <div
      ref={containerRef}
      onScroll={handleScroll}
      className={cn(SURFACE, 'flex-1 overflow-y-auto px-3 py-3')}
    >
      {messages.length === 0 && (
        <div className="flex flex-col items-center justify-center text-center py-12 px-8">
          <span className="w-12 h-12 rounded-xl bg-highlight text-primary/60 flex items-center justify-center mb-3">
            <FiMessageCircle className="w-6 h-6" />
          </span>
          <p className="text-sm text-primary/70">No messages yet.</p>
          <p className="text-xs text-primary/40 mt-1">Say hello — everyone here will see it.</p>
        </div>
      )}

      {messages.map((message, index) => {
        const isMine = message.sender?.id === member?.id;
        const previous = messages[index - 1];
        const day = dayLabel(message.createdAt, timezone);
        const showDay = day !== lastDay;
        lastDay = day;

        // Consecutive messages from the same person within 5 minutes are grouped
        // under one header, so a rapid exchange does not repeat the name on
        // every line.
        const isReply = !!message.parentMessageId;

        const grouped =
          !showDay &&
          !isReply &&
          previous &&
          !previous.parentMessageId &&
          previous.sender?.id === message.sender?.id &&
          new Date(message.createdAt) - new Date(previous.createdAt) < 5 * 60 * 1000;

        const actionable = !message.deletedAt && !message.pending && !message.failed;
        const showActions = actionable && touchedId === message.id;

        const hasAttachments = !message.deletedAt && message.attachments?.length > 0;
        // An attachment-only message gets no bubble — the file card is the
        // message. Anything else always gets one, even when the body is empty.
        const showBubble = Boolean(message.deletedAt || message.body || !hasAttachments);

        const bubbleTone = message.failed
          ? 'bg-alert/10 text-alert border border-alert/25'
          : isMine
            ? 'bg-highlight text-primary border border-primary/15'
            : 'bg-white text-primary/90 border border-neutral-300';

        const quote = isReply ? (
          <ReplyQuote
            replyTo={message.replyTo}
            onOpen={() => openThreadById(message.parentMessageId)}
          />
        ) : null;

        return (
          <div key={message.id || message.clientMsgId}>
            {showDay && (
              <div className="flex items-center gap-3 my-5">
                <div className="flex-1 h-px bg-neutral-300" />
                <span className="font-serif text-xs uppercase tracking-[0.12em] text-primary/50">
                  {day}
                </span>
                <div className="flex-1 h-px bg-neutral-300" />
              </div>
            )}

            <div
              {...(actionable ? longPress : {})}
              onPointerDown={(event) => {
                // A touch anywhere else dismisses an open bar.
                if (touchedId && touchedId !== message.id) setTouchedId(null);
                if (!actionable) return;
                pressedId.current = message.id;
                longPress.onPointerDown(event);
              }}
              className={cn(
                'group/msg relative flex gap-2',
                isMine ? 'flex-row-reverse' : 'flex-row',
                grouped ? 'mt-1' : 'mt-4'
              )}
            >
              {actionable && (
                <div
                  className={cn(
                    // Overlaid rather than laid out: an in-flow wrapper would
                    // add a gap to the row and shift the bubbles sideways.
                    'absolute inset-0 pointer-events-none transition-opacity',
                    // Hover on a pointer device, long-press on touch.
                    showActions
                      ? 'opacity-100'
                      : 'opacity-0 group-hover/msg:opacity-100 focus-within:opacity-100'
                  )}
                >
                  <MessageActions
                    visible
                    isMine={isMine}
                    // Anchored to the member's own side of the panel, so the bar
                    // never floats over empty space opposite the bubble.
                    side={isMine ? 'right' : 'left'}
                    isPinned={!!message.isPinned}
                    onReact={(emoji) => toggleReaction(message.id, emoji)}
                    onReply={() => openThread(message)}
                    onTogglePin={async () => {
                      const result = await togglePin(message);
                      if (!result.ok) toast.error(result.message);
                    }}
                    onDelete={() => {
                      if (window.confirm('Delete this message for everyone?')) {
                        chatService.deleteMessage(message.id).catch(() => {});
                      }
                    }}
                    onReport={() => reportMessage(message)}
                    onDismiss={() => setTouchedId(null)}
                  />
                </div>
              )}

              {/* The column is reserved even for a grouped message, so a run of
                  messages from one person stays aligned under their avatar. */}
              {!isMine && (
                <div className="w-7 shrink-0 self-end pb-0.5">
                  {!grouped && (
                    <Avatar
                      name={message.sender?.displayName}
                      photoUrl={message.sender?.photoUrl}
                      seed={message.sender?.id}
                      size="sm"
                    />
                  )}
                </div>
              )}

              <div
                className={cn(
                  'flex flex-col min-w-0 max-w-[80%]',
                  isMine ? 'items-end' : 'items-start'
                )}
              >
                {!grouped && (
                  <div className={cn('flex items-baseline gap-2 mb-1 px-0.5', isMine && 'flex-row-reverse')}>
                    <span className="font-serif text-sm font-semibold text-primary">
                      {isMine ? 'You' : message.sender?.displayName || 'Unknown member'}
                    </span>
                    <span className="text-[11px] text-primary/40">
                      {formatTime(message.createdAt, timezone)}
                    </span>
                  </div>
                )}

                {showBubble ? (
                  <div
                    className={cn(
                      BUBBLE_BASE,
                      bubbleTone,
                      message.pending && 'opacity-70'
                    )}
                  >
                    {quote}

                    {message.deletedAt ? (
                      <p className="text-sm italic text-primary/40">
                        This message was deleted.
                      </p>
                    ) : (
                      <p
                        // Message bodies are stored raw and rendered as text — never
                        // as HTML. whitespace-pre-wrap preserves line breaks without
                        // needing markup, which keeps the XSS surface at zero.
                        className="text-sm whitespace-pre-wrap break-words"
                      >
                        {message.body}
                      </p>
                    )}

                    {message.editedAt && !message.deletedAt && (
                      <span className="block text-[10px] mt-0.5 text-primary/35">edited</span>
                    )}
                  </div>
                ) : (
                  quote
                )}

                {hasAttachments && (
                  <div className={cn('flex flex-col', isMine ? 'items-end' : 'items-start')}>
                    <AttachmentList
                      attachments={message.attachments}
                      uploading={message.uploading}
                      progress={message.progress || 0}
                    />
                  </div>
                )}

                {!message.deletedAt && !message.pending && !message.failed && (
                  <div className={cn('flex w-full', isMine && 'justify-end')}>
                    <ReactionBar messageId={message.id} showAdd={false} />
                  </div>
                )}

                {message.failed && (
                  <span className="flex items-center gap-1 text-[11px] text-alert mt-1">
                    <FiAlertCircle className="w-3 h-3" /> Not sent
                  </span>
                )}

                {message.replyCount > 0 && (
                  <button
                    onClick={() => openThread(message)}
                    className="mt-1 text-[11px] font-medium text-secondary border-b border-secondary/40
                               hover:text-primary hover:border-primary transition-colors"
                  >
                    {message.replyCount} {message.replyCount === 1 ? 'reply' : 'replies'}
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })}

      {typingMembers.length > 0 && (
        <div className="flex items-center gap-2 mt-3 pl-9">
          <span className="flex items-center gap-1 px-3 py-2 rounded-[10px]
                           bg-white border border-neutral-300">
            {TYPING_DOTS.map((delay) => (
              <span
                key={delay}
                className="w-1.5 h-1.5 rounded-full bg-secondary animate-bounce"
                style={{ animationDelay: `${delay}ms` }}
              />
            ))}
          </span>
          <span className="text-[11px] text-primary/45 truncate">
            {typingMembers.map((m) => m.displayName).join(', ')}
            {typingMembers.length === 1 ? ' is' : ' are'} typing…
          </span>
        </div>
      )}

      <div ref={bottomRef} />
    </div>
  );
}
